// glrenderer.ts — the WebGL2 PRESENTATION renderer engine (plan: "same
// compositing math, WebGL2 presentation").
//
// The C++ web client's DibBitmaps are SDL target textures and every pixel op is
// a GPU pass (game/bitmap.cpp SDL arm; game/sdl/display.cpp present). This
// engine reproduces that architecture over WebGL2 while matching the CPU
// reference surface (gfx/dibbitmap.ts CpuDibBitmap) BYTE-FOR-BYTE:
//
//   - NO fixed-function blending. Every op computes the CpuDibBitmap formula in
//     the fragment shader with EXACT 32-bit integer arithmetic (texelFetch in,
//     round-half-up integer division out). q() is Math.round over non-negative
//     float64; none of the formulas can land on an exact .5 (every numerator/
//     denominator pair has opposite parity), and float64's ~1e-13 error cannot
//     bridge the >=1/(2*255^2) gap to a rounding boundary — so integer
//     round-half-up == the CPU's float64+Math.round, bit for bit. Fixed-function
//     blending (float32, implementation-defined rounding) provably CAN flip
//     these roundings — that's why it isn't used.
//   - Ops that read the destination (fractional-alpha BLEND pixels, the
//     additive team-color decals, Shadow, self-blits) sample a per-surface
//     SNAPSHOT texture kept fresh by copyTexSubImage2D hazard syncs — WebGL2
//     forbids sampling the bound render target (feedback loop).
//   - Sources: pack atlas images become static textures with a 16x16-block
//     alpha classification, so binary/opaque art (the overwhelming majority —
//     see dibbitmap.ts's header) skips the dst-read path entirely; small CPU
//     PixelSources (terrain tiles, AnimSprite captures, TBitmap::Flash scratch)
//     pack into a shared scratch atlas; GL surfaces blit surface-to-surface.
//   - Integer-aligned quads only, texelFetch sampling — nothing filtered or
//     subpixel (the plan's pixel-exactness precondition). The 1:1 source
//     mapping is computed from gl_FragCoord (sp = srcOffset + dstPixel), so
//     clipping the quad geometry never shifts the mapping; per-op clip rects
//     (sprite clip, line clip) are pure geometry intersections — no scissor.
//
// Draw order is preserved exactly: ops append to one global instanced batch
// (one draw call per flush); flushes happen on target switch / texture-slot
// pressure / capacity, and before any dst-read whose rect intersects pixels
// drawn since the snapshot was last synced. Batched quads never blend, so
// GL's primitive-order guarantee gives painter's order within a batch.

import type { AtlasImage, Color } from '../texatlasmgr.ts';
import type { Rect } from '../texatlasmgr.ts';
import type { PixelSource } from '../dibbitmap.ts';
import { GlDibBitmap } from './glsurface.ts';

// ---- op kinds (must match the fragment shader) -------------------------------
export const KIND_FILL = 0; // opaque rect: out = (clr, 255)
export const KIND_BLEND = 1; // Blt/BltModulated: src*mod*a + dst*(1-a) (mod=255 for Blt)
export const KIND_TINTED = 2; // BltTinted: dst + src*mod*a (ColorMod + ADD)
export const KIND_SHADOW = 3; // Shadow: dst*(155/255), a: 100 + a*(155/255)
export const KIND_SCALED = 4; // AnimSprite scaled copy (trunc'd coords, alpha-keyed)
const MODE_READS_DST = 64;

// alpha classification of a source region
export const CLASS_OPAQUE = 0; // every a == 255
export const CLASS_TRANS = 1; // every a == 0
export const CLASS_BINARY = 2; // only 0/255 — BLEND degenerates to copy/skip
export const CLASS_MIXED = 3; // fractional alpha present -> dst-read path

const MAX_INSTANCES = 8192;
const STRIDE = 88; // bytes/instance: vec4 f32 + ivec4 + ivec4 + ivec2 + ivec4 + ivec4
const SCRATCH_PAGE = 1024; // scratch atlas page dimension
const SCRATCH_MAX = 256; // sources larger than this get their own texture
const MAX_SLOTS = 7; // dynamic source slots (units 1..7; 0 = snapshot)

// A client-telemetry sink (main.ts wires it to web/telemetry.track). Called on WebGL2 texture
// allocation failure and context loss/restore so those show up in the wild; undefined for the
// oracle/parity harnesses (which run headless and must stay byte- and timing-identical — the
// getError() OOM probe in newTexture is skipped entirely when it's unset).
export type GlDiag = (kind: string, detail?: Record<string, unknown>) => void;
const STALE_CAP = 64; // staleRects entries before collapsing to a bbox

const VS = `#version 300 es
layout(location=0) in vec2 a_corner;
layout(location=1) in vec4 a_dst;
layout(location=2) in ivec4 a_src;
layout(location=3) in ivec4 a_bounds;
layout(location=4) in ivec2 a_misc;
layout(location=5) in ivec4 a_src2;
layout(location=6) in ivec4 a_bounds2;
uniform vec2 u_res;
flat out ivec4 v_src;
flat out ivec4 v_bounds;
flat out ivec2 v_misc;
flat out ivec4 v_src2;
flat out ivec4 v_bounds2;
void main() {
  vec2 p = a_dst.xy + a_corner * a_dst.zw;
  gl_Position = vec4(p * 2.0 / u_res - 1.0, 0.0, 1.0);
  v_src = a_src;
  v_bounds = a_bounds;
  v_misc = a_misc;
  v_src2 = a_src2;
  v_bounds2 = a_bounds2;
}`;

// The fragment shader IS dibbitmap.ts in integer form. round(n/D) with inputs
// non-negative == (2n+D)/(2D) in integer division. Attribute semantics per kind:
//   BLEND/TINTED: a_src.xy = srcOffset (sp = off + dstPixel); a_bounds = valid
//                 texel region [x0,y0,x1,y1) -- the source image extent
//                 intersected with the blit's mapped rect; outside behaves as
//                 alpha 0 (blitCore's source-bounds skip).
//   SCALED:       a_src = (srcCx, srcCy, dstW, dstH); a_bounds = (dstOriginX,
//                 dstOriginY, srcOx, srcOy) -- sprites.ts:188/193 trunc+clamp.
//   FILL/SHADOW:  unused.
//   FUSION:       a_src2 = (off2x, off2y, mode2, color2) + a_bounds2 carry a
//                 SECOND 1:1 layer (mode2=0 none): a dependent op (the team
//                 decal over its base, an overlapping neighbor sprite)
//                 collapsed into this quad. The intermediate is quantized
//                 exactly like the two-pass result, so fusion is byte-neutral;
//                 it exists to kill the per-gob flush + snapshot-sync the
//                 dst-read dependency would otherwise force.
const FS = `#version 300 es
precision highp float;
precision highp int;
uniform sampler2D u_snap;
uniform sampler2D u_t1;
uniform sampler2D u_t2;
uniform sampler2D u_t3;
uniform sampler2D u_t4;
uniform sampler2D u_t5;
uniform sampler2D u_t6;
uniform sampler2D u_t7;
flat in ivec4 v_src;
flat in ivec4 v_bounds;
flat in ivec2 v_misc;
flat in ivec4 v_src2;
flat in ivec4 v_bounds2;
out vec4 o_color;

ivec4 px(int slot, ivec2 p) {
  vec4 c;
  if (slot < 2)      c = (slot == 0) ? texelFetch(u_snap, p, 0) : texelFetch(u_t1, p, 0);
  else if (slot < 4) c = (slot == 2) ? texelFetch(u_t2, p, 0) : texelFetch(u_t3, p, 0);
  else if (slot < 6) c = (slot == 4) ? texelFetch(u_t4, p, 0) : texelFetch(u_t5, p, 0);
  else               c = (slot == 6) ? texelFetch(u_t6, p, 0) : texelFetch(u_t7, p, 0);
  return ivec4(c * 255.0 + 0.5);
}
int rd255(int n) { return (n * 2 + 255) / 510; }        // round(n/255)
int rd65025(int n) { return (n * 2 + 65025) / 130050; } // round(n/65025)

// BLEND -- dibbitmap.ts:66-79 (Blt; m=255) / :98-110 (BltModulated)
ivec4 blend(ivec4 s, ivec3 m, ivec4 d) {
  if (s.a == 0) return d;
  return ivec4(
    rd65025(s.r * m.r * s.a + d.r * 255 * (255 - s.a)),
    rd65025(s.g * m.g * s.a + d.g * 255 * (255 - s.a)),
    rd65025(s.b * m.b * s.a + d.b * 255 * (255 - s.a)),
    rd255(255 * s.a + d.a * (255 - s.a)));
}
// TINTED -- dibbitmap.ts:82-95 (ColorMod + ADD; clamps)
ivec4 tinted(ivec4 s, ivec3 m, ivec4 d) {
  if (s.a == 0) return d;
  return ivec4(
    clamp(rd65025(d.r * 65025 + s.r * m.r * s.a), 0, 255),
    clamp(rd65025(d.g * 65025 + s.g * m.g * s.a), 0, 255),
    clamp(rd65025(d.b * 65025 + s.b * m.b * s.a), 0, 255),
    clamp(rd255(d.a * 255 + s.a * s.a), 0, 255));
}

void main() {
  ivec2 dp = ivec2(gl_FragCoord.xy);
  int mode = v_misc.x;
  int kind = mode & 7;
  int slot = (mode >> 3) & 7;
  bool readsDst = (mode & 64) != 0;
  ivec3 m = ivec3(v_misc.y & 255, (v_misc.y >> 8) & 255, (v_misc.y >> 16) & 255);
  ivec4 d = readsDst ? ivec4(texelFetch(u_snap, dp, 0) * 255.0 + 0.5) : ivec4(0);

  if (kind == 0) { // FILL -- dibbitmap.ts:113-123 (opaque, alpha 255)
    o_color = vec4(vec3(m) / 255.0, 1.0);
    return;
  }
  if (kind == 3) { // SHADOW -- dibbitmap.ts:129-142
    o_color = vec4(float(rd255(d.r * 155)), float(rd255(d.g * 155)),
      float(rd255(d.b * 155)), float(rd255(25500 + d.a * 155))) / 255.0;
    return;
  }
  if (kind == 4) { // SCALED -- sprites.ts:184-201 (trunc'd coords, clamp, a-keyed copy)
    ivec2 local = dp - v_bounds.xy;
    ivec2 sp = v_bounds.zw + ivec2(
      clamp((local.x * v_src.x) / v_src.z, 0, v_src.x - 1),
      clamp((local.y * v_src.y) / v_src.w, 0, v_src.y - 1));
    ivec4 s = px(slot, sp);
    if (s.a == 0) discard;
    o_color = vec4(vec3(s.rgb) / 255.0, 1.0);
    return;
  }

  // 1:1 source ops; out-of-source pixels behave as alpha 0 (blitCore's skip)
  int mode2 = v_src2.z;
  ivec2 sp = v_src.xy + dp;
  bool inb = sp.x >= v_bounds.x && sp.y >= v_bounds.y && sp.x < v_bounds.z && sp.y < v_bounds.w;
  ivec4 s = inb ? px(slot, sp) : ivec4(0);
  // cosmetic per-sprite opacity: the free top byte of the color word fades source coverage
  // (0 = none = opaque; readsDst is forced on the JS side so this blends with the terrain).
  int op1 = (v_misc.y >> 24) & 255;
  if (op1 != 0) s.a = (s.a * op1) / 255;
  if (mode2 == 0 && !readsDst && s.a == 0) discard; // binary art's skip
  ivec4 cur = (kind == 2) ? tinted(s, m, d) : blend(s, m, d);

  if (mode2 != 0) { // the fused second layer
    int slot2 = (mode2 >> 3) & 7;
    ivec3 m2 = ivec3(v_src2.w & 255, (v_src2.w >> 8) & 255, (v_src2.w >> 16) & 255);
    ivec2 sp2 = v_src2.xy + dp;
    bool inb2 = sp2.x >= v_bounds2.x && sp2.y >= v_bounds2.y &&
      sp2.x < v_bounds2.z && sp2.y < v_bounds2.w;
    ivec4 s2 = inb2 ? px(slot2, sp2) : ivec4(0);
    int op2 = (v_src2.w >> 24) & 255;
    if (op2 != 0) s2.a = (s2.a * op2) / 255;
    cur = ((mode2 & 7) == 2) ? tinted(s2, m2, cur) : blend(s2, m2, cur);
  }
  o_color = vec4(cur) / 255.0;
}`;

export interface SurfaceData {
  tex: WebGLTexture;
  fbo: WebGLFramebuffer;
  snap: WebGLTexture | null;
  cx: number;
  cy: number;
  /** regions drawn since the snapshot was last synced (snapshot fresh elsewhere). */
  staleRects: Rect[];
  alive: boolean;
}

/** a resolved source image living in some texture */
export interface ResolvedSrc {
  /** null = the target surface's snapshot (self-source) */
  tex: WebGLTexture | null;
  ox: number; // texture coords of the source image's (0,0)
  oy: number;
  scx: number; // source image extent (blitCore's src.cx/cy)
  scy: number;
  cls: number; // CLASS_* over the whole source image
  classMap: { bw: number; bh: number; blocks: Uint8Array } | null; // atlas block classes
}

interface ScratchSlot {
  tex: WebGLTexture;
  ox: number;
  oy: number;
  own: boolean;
}

const intersects = (a: Rect, b: Rect): boolean =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

export class GlRenderer {
  /** Monotonic: bumps whenever any surface's visible content changes (every
   *  non-empty flush). The presenter skips frames where it hasn't moved —
   *  a static screen issues ZERO GL work per rAF (battery). */
  drawSerial = 0;
  readonly gl: WebGL2RenderingContext;
  private prog!: WebGLProgram;
  private uRes!: WebGLUniformLocation;
  private vao!: WebGLVertexArrayObject;
  private instBuf!: WebGLBuffer;
  private dummyTex!: WebGLTexture;

  // the single global pending batch
  private target: SurfaceData | null = null;
  private count = 0;
  private readonly bytes = new ArrayBuffer(MAX_INSTANCES * STRIDE);
  private readonly f32 = new Float32Array(this.bytes);
  private readonly i32 = new Int32Array(this.bytes);
  private slotTex: WebGLTexture[] = [];
  private pendingRects: Rect[] = [];
  private pendingGrid = new Map<number, number[]>();

  // texture caches
  private readonly atlasSrcs = new WeakMap<AtlasImage, ResolvedSrc>();
  private readonly pixelSrcs = new WeakMap<PixelSource, ResolvedSrc>();
  private readonly reg = new FinalizationRegistry<() => void>((free) => free());

  // scratch atlas pages (shelf allocator + exact-size free lists)
  private pages: { tex: WebGLTexture; shelfY: number; shelfH: number; shelfX: number }[] = [];
  private readonly freeSlots = new Map<string, ScratchSlot[]>();

  // observability (the oracle/perf harnesses read these)
  readonly stats = { draws: 0, syncs: 0, instances: 0, uploads: 0, fused: 0 };

  // client-telemetry sink + texture-allocation accounting (see GlDiag). oomReported throttles the OOM
  // report to once per renderer; texBytes is the running RGBA8 texture footprint reported alongside it.
  private onDiag?: GlDiag;
  private oomReported = false;
  private texBytes = 0;

  private constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
  }

  /** Create over the game canvas (present blits to it) or any canvas (oracle). onDiag, when given,
   *  receives WebGL2 texture-allocation-failure and context-loss events for telemetry. */
  static create(canvas: HTMLCanvasElement, onDiag?: GlDiag): GlRenderer {
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      // TRUE so a frame with no present legally retains the last composited
      // image (the spec clears the buffer after compositing otherwise — the
      // Safari black-screen class). The idle-present battery gate (main.ts:
      // present only when drawSerial moved) relies on this.
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    }) as WebGL2RenderingContext | null;
    if (gl === null) throw new Error('webgl2 context unavailable');
    const r = new GlRenderer(gl);
    r.onDiag = onDiag;
    r.init();
    return r;
  }

  private init(): void {
    const gl = this.gl;
    // Context loss (GPU eviction — common on low-memory mobile) blanks the canvas silently otherwise;
    // preventDefault keeps the context eligible for restore. Reported for visibility in the wild.
    const cnv = gl.canvas as unknown as {
      addEventListener?: (t: string, cb: (e: Event) => void) => void;
    };
    cnv.addEventListener?.('webglcontextlost', (e: Event) => {
      e.preventDefault?.();
      this.onDiag?.('webgl2_context_lost');
    });
    cnv.addEventListener?.('webglcontextrestored', () => {
      this.onDiag?.('webgl2_context_restored');
    });
    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.DITHER); // bit-exact unorm writes
    gl.disable(gl.SCISSOR_TEST);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

    const compile = (type: number, src: string): WebGLShader => {
      const sh = gl.createShader(type)!;
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        throw new Error(`shader compile: ${gl.getShaderInfoLog(sh)}`);
      }
      return sh;
    };
    const prog = gl.createProgram()!;
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VS));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      throw new Error(`program link: ${gl.getProgramInfoLog(prog)}`);
    }
    this.prog = prog;
    gl.useProgram(prog);
    this.uRes = gl.getUniformLocation(prog, 'u_res')!;
    gl.uniform1i(gl.getUniformLocation(prog, 'u_snap'), 0);
    for (let i = 1; i <= MAX_SLOTS; i++) {
      gl.uniform1i(gl.getUniformLocation(prog, `u_t${i}`), i);
    }

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);

    const cornerBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, cornerBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this.instBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.bytes.byteLength, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 4, gl.FLOAT, false, STRIDE, 0);
    gl.vertexAttribDivisor(1, 1);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribIPointer(2, 4, gl.INT, STRIDE, 16);
    gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribIPointer(3, 4, gl.INT, STRIDE, 32);
    gl.vertexAttribDivisor(3, 1);
    gl.enableVertexAttribArray(4);
    gl.vertexAttribIPointer(4, 2, gl.INT, STRIDE, 48);
    gl.vertexAttribDivisor(4, 1);
    gl.enableVertexAttribArray(5);
    gl.vertexAttribIPointer(5, 4, gl.INT, STRIDE, 56);
    gl.vertexAttribDivisor(5, 1);
    gl.enableVertexAttribArray(6);
    gl.vertexAttribIPointer(6, 4, gl.INT, STRIDE, 72);
    gl.vertexAttribDivisor(6, 1);

    this.dummyTex = this.newTexture(1, 1); // valid binding for unused slots
  }

  private newTexture(cx: number, cy: number, data: Uint8Array | null = null): WebGLTexture {
    const gl = this.gl;
    const tex = gl.createTexture()!;
    gl.activeTexture(gl.TEXTURE0 + 15); // upload unit; flush rebinds 0..7
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA8,
      cx,
      cy,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      data === null ? null : data,
    );
    // Texture allocation is where a low-memory device fails silently (a black/incomplete texture). Only
    // probe when a diag sink is attached (production) — getError() forces a GPU sync, so the oracle and
    // parity harnesses (no onDiag) keep their exact timing. Reported once per renderer.
    this.texBytes += cx * cy * 4;
    if (this.onDiag !== undefined && !this.oomReported && gl.getError() === gl.OUT_OF_MEMORY) {
      this.oomReported = true;
      this.onDiag('webgl2_texture_alloc_failed', {
        cx,
        cy,
        bytes: cx * cy * 4,
        totalMb: Math.round(this.texBytes / 1048576),
      });
    }
    return tex;
  }

  // ---- surfaces ---------------------------------------------------------------

  createSurface(cx: number, cy: number): GlDibBitmap {
    const gl = this.gl;
    const tex = this.newTexture(cx, cy); // WebGL zero-initializes (CpuDibBitmap parity)
    const fbo = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    this.fboBound = fbo;
    const data: SurfaceData = {
      tex,
      fbo,
      snap: null,
      cx,
      cy,
      staleRects: [{ left: 0, top: 0, right: cx, bottom: cy }],
      alive: true,
    };
    const surf = new GlDibBitmap(this, data, cx, cy);
    this.reg.register(surf, () => this.destroySurface(data));
    return surf;
  }

  destroySurface(s: SurfaceData): void {
    if (!s.alive) return;
    s.alive = false;
    if (this.target === s) {
      this.target = null;
      this.pendingReset();
    }
    const gl = this.gl;
    gl.deleteFramebuffer(s.fbo);
    gl.deleteTexture(s.tex);
    if (s.snap !== null) gl.deleteTexture(s.snap);
  }

  // ---- batching core ------------------------------------------------------------

  private pendingReset(): void {
    this.count = 0;
    this.pendingRects.length = 0;
    this.pendingGrid.clear();
    this.slotTex.length = 0;
  }

  // GL state caches (flush is the hot path: keep redundant binds out of it)
  private fboBound: WebGLFramebuffer | null | undefined = undefined;
  private readonly texBound: (WebGLTexture | null)[] = new Array(8).fill(null);
  private resW = -1;
  private resH = -1;

  private bindTargetFbo(fbo: WebGLFramebuffer, cx: number, cy: number): void {
    const gl = this.gl;
    if (this.fboBound !== fbo) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      this.fboBound = fbo;
    }
    if (this.resW !== cx || this.resH !== cy) {
      gl.viewport(0, 0, cx, cy);
      gl.uniform2f(this.uRes, cx, cy);
      this.resW = cx;
      this.resH = cy;
    }
  }

  private bindUnit(unit: number, tex: WebGLTexture): void {
    if (this.texBound[unit] === tex) return;
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    this.texBound[unit] = tex;
  }

  /** Draw everything queued. Safe to call anytime. */
  flush(): void {
    if (this.count === 0 || this.target === null) {
      this.pendingReset();
      return;
    }
    const gl = this.gl;
    const t = this.target;
    this.bindTargetFbo(t.fbo, t.cx, t.cy);
    this.bindUnit(0, t.snap ?? this.dummyTex);
    for (let i = 0; i < this.slotTex.length; i++) {
      this.bindUnit(1 + i, this.slotTex[i]);
    }
    // a STALE cached binding of the target's own texture (it was a source for
    // an earlier batch) trips WebGL's feedback-loop validation even when no
    // quad samples that unit -- evict it
    for (let u = 0; u <= MAX_SLOTS; u++) {
      if (this.texBound[u] === t.tex) this.bindUnit(u, this.dummyTex);
    }
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, new Uint8Array(this.bytes, 0, this.count * STRIDE));
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.count);
    this.drawSerial++; // visible content changed — the present gate watches this
    this.stats.draws++;
    this.stats.instances += this.count;

    // drawn regions are now stale in the snapshot
    for (const r of this.pendingRects) t.staleRects.push(r);
    if (t.staleRects.length > STALE_CAP) {
      t.staleRects = [bbox(t.staleRects)];
    }
    this.pendingReset();
  }

  /** Copy the stale region of the target's framebuffer into its snapshot. */
  private syncSnapshot(t: SurfaceData): void {
    if (t.snap === null) t.snap = this.newTexture(t.cx, t.cy);
    if (t.staleRects.length === 0) return;
    const gl = this.gl;
    const r = bbox(t.staleRects);
    t.staleRects = [];
    const l = Math.max(0, r.left),
      tp = Math.max(0, r.top);
    const rr = Math.min(t.cx, r.right),
      b = Math.min(t.cy, r.bottom);
    if (rr <= l || b <= tp) return;
    if (this.fboBound !== t.fbo) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, t.fbo);
      this.fboBound = t.fbo;
    }
    gl.activeTexture(gl.TEXTURE0 + 15);
    gl.bindTexture(gl.TEXTURE_2D, t.snap);
    gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, l, tp, l, tp, rr - l, b - tp);
    this.stats.syncs++;
  }

  private gridCells(r: Rect, out: number[]): number[] {
    out.length = 0;
    const x0 = Math.max(0, r.left >> 6); // GRID_CELL = 64
    const y0 = Math.max(0, r.top >> 6);
    const x1 = Math.max(0, (r.right - 1) >> 6);
    const y1 = Math.max(0, (r.bottom - 1) >> 6);
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) out.push(y * 1024 + x);
    }
    return out;
  }

  private readonly cellScratch: number[] = [];
  private readonly conflictScratch: number[] = [];

  /** pending-quad indices whose rects intersect r (deduped, ascending-ish) */
  private pendingConflicts(r: Rect): number[] {
    const out = this.conflictScratch;
    out.length = 0;
    for (const cell of this.gridCells(r, this.cellScratch)) {
      const list = this.pendingGrid.get(cell);
      if (list === undefined) continue;
      for (const i of list) {
        if (intersects(this.pendingRects[i], r) && !out.includes(i)) out.push(i);
      }
    }
    return out;
  }

  private staleIntersects(t: SurfaceData, r: Rect): boolean {
    for (const s of t.staleRects) {
      if (intersects(s, r)) return true;
    }
    return false;
  }

  private addPendingRect(geom: Rect, idx: number): void {
    for (const cell of this.gridCells(geom, this.cellScratch)) {
      const list = this.pendingGrid.get(cell);
      if (list === undefined) this.pendingGrid.set(cell, [idx]);
      else if (!list.includes(idx)) list.push(idx);
    }
  }

  /**
   * Queue one quad. geom must already be clipped to the surface (and any op
   * clip rect); readRects are regions of the TARGET surface the op samples
   * (dst pixels and/or a self-source) — they force a snapshot sync when they
   * overlap anything drawn since the last sync. fusable 1:1 dst-readers whose
   * ONLY conflict is the immediately-preceding quad get folded into it as a
   * second shader layer instead (byte-neutral; see the FS FUSION note).
   */
  enqueue(
    t: SurfaceData,
    geom: Rect,
    srcTex: WebGLTexture | null /* null = the snapshot (self-src) */,
    s0: number,
    s1: number,
    s2: number,
    s3: number,
    b0: number,
    b1: number,
    b2: number,
    b3: number,
    kind: number,
    readsDst: boolean,
    colorPacked: number,
    readRects: readonly Rect[],
  ): void {
    if (geom.right <= geom.left || geom.bottom <= geom.top) return;
    if (this.target !== t) {
      this.flush();
      this.target = t;
    }

    if (readRects.length > 0) {
      let dirty = t.snap === null;
      if (!dirty) {
        for (const r of readRects) {
          if (this.staleIntersects(t, r)) {
            dirty = true;
            break;
          }
          const conflicts = this.pendingConflicts(r);
          if (conflicts.length === 0) continue;
          // ---- the fusion path: a 1:1 dst-reading op whose only conflict is
          // the LAST pending quad — and that quad is an unfused, non-self 1:1
          // op — becomes its second layer. Conditions guarantee the snapshot
          // is fresh across the union and order is preserved exactly.
          if (
            (kind === KIND_BLEND || kind === KIND_TINTED) &&
            srcTex !== null &&
            readRects.length === 1 &&
            conflicts.length === 1 &&
            conflicts[0] === this.count - 1 &&
            this.count > 0
          ) {
            const lo = (this.count - 1) * (STRIDE / 4);
            const lmode = this.i32[lo + 12];
            const lkind = lmode & 7;
            const lslot = (lmode >> 3) & 7;
            if (
              (lkind === KIND_BLEND || lkind === KIND_TINTED) &&
              lslot !== 0 &&
              this.i32[lo + 16] === 0 /* no layer2 yet */
            ) {
              const union: Rect = {
                left: Math.min(geom.left, this.f32[lo]),
                top: Math.min(geom.top, this.f32[lo + 1]),
                right: Math.max(geom.right, this.f32[lo] + this.f32[lo + 2]),
                bottom: Math.max(geom.bottom, this.f32[lo + 1] + this.f32[lo + 3]),
              };
              // the fused quad keeps dst everywhere it doesn't write — the
              // snapshot must be fresh over the WHOLE union, and nothing else
              // pending may overlap it
              const uconf = this.staleIntersects(t, union) ? null : this.pendingConflicts(union);
              if (uconf !== null && uconf.length === 1 && uconf[0] === this.count - 1) {
                // slot for the second layer (no flush allowed here)
                let slot2 = this.slotTex.indexOf(srcTex) + 1;
                if (slot2 === 0 && this.slotTex.length < MAX_SLOTS) {
                  this.slotTex.push(srcTex);
                  slot2 = this.slotTex.length;
                }
                if (slot2 !== 0) {
                  this.f32[lo] = union.left;
                  this.f32[lo + 1] = union.top;
                  this.f32[lo + 2] = union.right - union.left;
                  this.f32[lo + 3] = union.bottom - union.top;
                  this.i32[lo + 12] = lmode | MODE_READS_DST;
                  this.i32[lo + 14] = s0;
                  this.i32[lo + 15] = s1;
                  this.i32[lo + 16] = kind | (slot2 << 3);
                  this.i32[lo + 17] = colorPacked;
                  this.i32[lo + 18] = b0;
                  this.i32[lo + 19] = b1;
                  this.i32[lo + 20] = b2;
                  this.i32[lo + 21] = b3;
                  this.pendingRects[this.count - 1] = union;
                  this.addPendingRect(union, this.count - 1);
                  this.stats.fused++;
                  return;
                }
              }
            }
          }
          dirty = true;
          break;
        }
      }
      if (dirty) {
        this.flush();
        this.syncSnapshot(t); // snapshot fresh EVERYWHERE afterward
      }
    }

    let slot = 0;
    if (srcTex !== null) {
      const i = this.slotTex.indexOf(srcTex);
      if (i >= 0) {
        slot = i + 1;
      } else {
        if (this.slotTex.length >= MAX_SLOTS) this.flush();
        this.slotTex.push(srcTex);
        slot = this.slotTex.length;
      }
    }

    if (this.count >= MAX_INSTANCES) this.flush();

    const o = this.count * (STRIDE / 4);
    this.f32[o] = geom.left;
    this.f32[o + 1] = geom.top;
    this.f32[o + 2] = geom.right - geom.left;
    this.f32[o + 3] = geom.bottom - geom.top;
    this.i32[o + 4] = s0;
    this.i32[o + 5] = s1;
    this.i32[o + 6] = s2;
    this.i32[o + 7] = s3;
    this.i32[o + 8] = b0;
    this.i32[o + 9] = b1;
    this.i32[o + 10] = b2;
    this.i32[o + 11] = b3;
    this.i32[o + 12] = kind | (slot << 3) | (readsDst ? MODE_READS_DST : 0);
    this.i32[o + 13] = colorPacked;
    this.i32[o + 14] = 0;
    this.i32[o + 15] = 0;
    this.i32[o + 16] = 0; // mode2 = none
    this.i32[o + 17] = 0;
    this.i32[o + 18] = 0;
    this.i32[o + 19] = 0;
    this.i32[o + 20] = 0;
    this.i32[o + 21] = 0;
    const idx = this.pendingRects.length;
    this.pendingRects.push(geom);
    this.addPendingRect(geom, idx);
    this.count++;
  }

  /** flush + make the surface's snapshot fully fresh (Scroll preamble). */
  freshSnapshot(t: SurfaceData): void {
    if (this.target !== t) {
      this.flush();
      this.target = t;
    } else {
      this.flush();
    }
    this.syncSnapshot(t);
  }

  // ---- source resolution ---------------------------------------------------------

  /** classify a whole rgba image */
  private static classify(rgba: Uint8Array): number {
    let any0 = false,
      any255 = false;
    for (let i = 3; i < rgba.length; i += 4) {
      const a = rgba[i];
      if (a === 0) any0 = true;
      else if (a === 255) any255 = true;
      else return CLASS_MIXED;
    }
    if (any0 && any255) return CLASS_BINARY;
    return any0 ? CLASS_TRANS : CLASS_OPAQUE;
  }

  /** an atlas image: own texture + a 16x16-block class map for per-rect queries */
  resolveAtlas(img: AtlasImage): ResolvedSrc {
    let r = this.atlasSrcs.get(img);
    if (r !== undefined) return r;
    const tex = this.newTexture(img.cx, img.cy, img.rgba);
    this.stats.uploads++;
    const bw = Math.ceil(img.cx / 16),
      bh = Math.ceil(img.cy / 16);
    const blocks = new Uint8Array(bw * bh);
    for (let by = 0; by < bh; by++) {
      for (let bx = 0; bx < bw; bx++) {
        const x1 = Math.min(img.cx, (bx + 1) * 16),
          y1 = Math.min(img.cy, (by + 1) * 16);
        let any0 = false,
          any255 = false,
          mixed = false;
        for (let y = by * 16; y < y1 && !mixed; y++) {
          let i = (y * img.cx + bx * 16) * 4 + 3;
          for (let x = bx * 16; x < x1; x++, i += 4) {
            const a = img.rgba[i];
            if (a === 0) any0 = true;
            else if (a === 255) any255 = true;
            else {
              mixed = true;
              break;
            }
          }
        }
        blocks[by * bw + bx] = mixed
          ? CLASS_MIXED
          : any0 && any255
            ? CLASS_BINARY
            : any0
              ? CLASS_TRANS
              : CLASS_OPAQUE;
      }
    }
    r = {
      tex,
      ox: 0,
      oy: 0,
      scx: img.cx,
      scy: img.cy,
      cls: CLASS_MIXED,
      classMap: { bw, bh, blocks },
    };
    this.atlasSrcs.set(img, r);
    return r;
  }

  /** class of a source sub-rect (conservative over the atlas block map) */
  static rectClass(r: ResolvedSrc, sl: number, st: number, sr: number, sb: number): number {
    const map = r.classMap;
    if (map === null) return r.cls;
    const bx0 = Math.max(0, Math.floor(sl / 16));
    const by0 = Math.max(0, Math.floor(st / 16));
    const bx1 = Math.min(map.bw - 1, Math.floor((sr - 1) / 16));
    const by1 = Math.min(map.bh - 1, Math.floor((sb - 1) / 16));
    let any0 = false,
      any255 = false;
    for (let by = by0; by <= by1; by++) {
      for (let bx = bx0; bx <= bx1; bx++) {
        const c = map.blocks[by * map.bw + bx];
        if (c === CLASS_MIXED) return CLASS_MIXED;
        if (c === CLASS_BINARY) {
          any0 = true;
          any255 = true;
        } else if (c === CLASS_TRANS) any0 = true;
        else any255 = true;
      }
    }
    return any0 && any255 ? CLASS_BINARY : any0 ? CLASS_TRANS : CLASS_OPAQUE;
  }

  /** a CPU PixelSource (tile, AnimSprite capture, Flash scratch): scratch slot.
   *  Contents are stable after first use (tiles are immutable; captures/Flash
   *  are freshly allocated per change) — cached for the object's lifetime. */
  resolveSource(src: PixelSource): ResolvedSrc {
    let r = this.pixelSrcs.get(src);
    if (r !== undefined) return r;
    const cls = GlRenderer.classify(src.rgba);
    const slot = this.allocScratch(src.cx, src.cy);
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + 15);
    gl.bindTexture(gl.TEXTURE_2D, slot.tex);
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      slot.ox,
      slot.oy,
      src.cx,
      src.cy,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      src.rgba,
    );
    this.stats.uploads++;
    r = { tex: slot.tex, ox: slot.ox, oy: slot.oy, scx: src.cx, scy: src.cy, cls, classMap: null };
    this.pixelSrcs.set(src, r);
    const key = `${src.cx}x${src.cy}`;
    this.reg.register(src, () => {
      if (slot.own) {
        this.gl.deleteTexture(slot.tex);
      } else {
        const list = this.freeSlots.get(key);
        if (list === undefined) this.freeSlots.set(key, [slot]);
        else list.push(slot);
      }
    });
    return r;
  }

  private allocScratch(cx: number, cy: number): ScratchSlot {
    if (cx > SCRATCH_MAX || cy > SCRATCH_MAX) {
      return { tex: this.newTexture(cx, cy), ox: 0, oy: 0, own: true };
    }
    const key = `${cx}x${cy}`;
    const list = this.freeSlots.get(key);
    if (list !== undefined && list.length > 0) return list.pop()!;
    const h = (cy + 15) & ~15;
    for (;;) {
      const p = this.pages[this.pages.length - 1];
      if (p !== undefined) {
        if (p.shelfH >= h && p.shelfX + cx <= SCRATCH_PAGE) {
          const s = { tex: p.tex, ox: p.shelfX, oy: p.shelfY, own: false };
          p.shelfX += cx;
          return s;
        }
        if (p.shelfY + p.shelfH + h <= SCRATCH_PAGE) {
          p.shelfY += p.shelfH;
          p.shelfH = h;
          p.shelfX = 0;
          continue;
        }
      }
      if (this.pages.length >= 8) {
        // pathological pressure: fall back to an individual texture
        return { tex: this.newTexture(cx, cy), ox: 0, oy: 0, own: true };
      }
      this.pages.push({
        tex: this.newTexture(SCRATCH_PAGE, SCRATCH_PAGE),
        shelfY: 0,
        shelfH: h,
        shelfX: 0,
      });
    }
  }

  // ---- present / readback ----------------------------------------------------------

  /** Blit a surface to the canvas (the GL swap; the only y-flip anywhere). */
  /** Present = a fullscreen textured draw into the default framebuffer
   *  (display.cpp:291-307 RenderCopy semantics). NOT blitFramebuffer: WebKit
   *  silently rejects blits into its default framebuffer (INVALID_OPERATION,
   *  no exception) — Safari composited black while Chrome tolerated it. The
   *  draw samples the persistent back texture with integer-exact nearest
   *  mapping (texelFetch), y-flipped for the canvas orientation. */
  present(s: SurfaceData): void {
    this.flush();
    const gl = this.gl;
    if (this.presentProg === null) this.initPresent();
    const cw = gl.drawingBufferWidth,
      ch = gl.drawingBufferHeight;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this.fboBound = undefined;
    gl.viewport(0, 0, cw, ch);
    gl.useProgram(this.presentProg);
    gl.activeTexture(gl.TEXTURE0 + 15);
    gl.bindTexture(gl.TEXTURE_2D, s.tex);
    gl.uniform4i(this.uPresentDims!, s.cx, s.cy, cw, ch);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    // restore the engine program; invalidate the viewport cache so the next
    // bindTargetFbo re-sets it even for a same-sized target
    gl.useProgram(this.prog);
    this.resW = -1;
    this.resH = -1;
  }

  private presentProg: WebGLProgram | null = null;
  private uPresentDims: WebGLUniformLocation | null = null;

  private initPresent(): void {
    const gl = this.gl;
    const compile = (type: number, src: string): WebGLShader => {
      const sh = gl.createShader(type)!;
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        throw new Error(`present shader compile: ${gl.getShaderInfoLog(sh)}`);
      }
      return sh;
    };
    const prog = gl.createProgram()!;
    gl.attachShader(
      prog,
      compile(
        gl.VERTEX_SHADER,
        `#version 300 es
void main() {
  vec2 p = vec2(gl_VertexID == 1 ? 3.0 : -1.0, gl_VertexID == 2 ? 3.0 : -1.0);
  gl_Position = vec4(p, 0.0, 1.0);
}`,
      ),
    );
    gl.attachShader(
      prog,
      compile(
        gl.FRAGMENT_SHADER,
        `#version 300 es
precision highp float; precision highp int;
uniform highp sampler2D u_ptex;
uniform ivec4 u_pdims; // surface cx, cy, canvas cw, ch
out vec4 o_color;
void main() {
  ivec2 fc = ivec2(gl_FragCoord.xy);
  int sx = (fc.x * u_pdims.x) / u_pdims.z;
  int sy = ((u_pdims.w - 1 - fc.y) * u_pdims.y) / u_pdims.w; // canvas top = row 0
  o_color = vec4(texelFetch(u_ptex, ivec2(sx, sy), 0).rgb, 1.0);
}`,
      ),
    );
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      throw new Error(`present link: ${gl.getProgramInfoLog(prog)}`);
    }
    this.presentProg = prog;
    gl.useProgram(prog);
    gl.uniform1i(gl.getUniformLocation(prog, 'u_ptex'), 15);
    this.uPresentDims = gl.getUniformLocation(prog, 'u_pdims');
    gl.useProgram(this.prog);
  }

  /** Exact surface bytes (row 0 = top), comparable 1:1 with CpuDibBitmap.rgba. */
  readback(s: SurfaceData): Uint8Array {
    this.flush();
    const gl = this.gl;
    const out = new Uint8Array(s.cx * s.cy * 4);
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, s.fbo);
    gl.readPixels(0, 0, s.cx, s.cy, gl.RGBA, gl.UNSIGNED_BYTE, out);
    this.fboBound = undefined; // READ binding invalidated the cache
    return out;
  }
}

function bbox(rects: readonly Rect[]): Rect {
  let l = rects[0].left,
    t = rects[0].top,
    r = rects[0].right,
    b = rects[0].bottom;
  for (const rc of rects) {
    if (rc.left < l) l = rc.left;
    if (rc.top < t) t = rc.top;
    if (rc.right > r) r = rc.right;
    if (rc.bottom > b) b = rc.bottom;
  }
  return { left: l, top: t, right: r, bottom: b };
}

export const packColor = (clr: Color): number =>
  (clr.r & 255) | ((clr.g & 255) << 8) | ((clr.b & 255) << 16);
